import {
  SYNC_ENTITIES,
  type SyncEntity,
  type SyncMutationResult,
  type SyncPullResponse,
} from '@malerbetrieb/shared';
import { api, ApiError, NetworkError } from '../api/client.js';
import { db, META_KEYS, readMeta, writeMeta, type ConflictEntry, type OutboxEntry } from '../offline/db.js';
import { newId, nowIso } from '../offline/repository.js';

/**
 * Synchronisation zwischen IndexedDB und Server.
 *
 * Regeln, die hier konsequent gelten:
 *  - Es wird nie etwas lokal gelöscht, nur weil eine Übertragung scheitert.
 *  - Ein Versionskonflikt führt zu einem sichtbaren Hinweis, nicht zu einem
 *    stillen Überschreiben – der eigene Stand bleibt im Konfliktdatensatz.
 *  - Läuft die Sitzung während der Offline-Arbeit ab, bleibt die
 *    Warteschlange vollständig erhalten und die App fordert eine erneute
 *    Anmeldung an.
 */

export type SyncPhase = 'idle' | 'pruefe' | 'sende' | 'empfange';

export interface SyncOutcome {
  status: 'ok' | 'offline' | 'anmeldung-noetig' | 'fehler';
  pushed: number;
  pulled: number;
  conflicts: number;
  message?: string;
}

const PUSH_BATCH_SIZE = 100;
const MAX_PULL_PAGES = 25;
/** Nach so vielen erfolglosen Versuchen wird ein Eintrag sichtbar gemeldet. */
const MAX_ATTEMPTS_BEFORE_REPORT = 5;

const TABLE_BY_ENTITY = {
  orders: db.orders,
  positions: db.positions,
  notes: db.notes,
  photos: db.photos,
} as const;

type AnyTable = {
  put(item: never): Promise<unknown>;
  bulkPut(items: never): Promise<unknown>;
  get(key: string): Promise<unknown>;
};

function tableFor(entity: SyncEntity): AnyTable {
  return TABLE_BY_ENTITY[entity] as unknown as AnyTable;
}

let laufend: Promise<SyncOutcome> | null = null;

/**
 * Führt eine vollständige Synchronisation aus. Parallele Aufrufe teilen sich
 * denselben Durchlauf, damit sich Timer, Online-Ereignis und Benutzeraktion
 * nicht gegenseitig ins Gehege kommen.
 */
export function synchronisieren(onPhase?: (phase: SyncPhase) => void): Promise<SyncOutcome> {
  if (laufend) return laufend;

  laufend = durchfuehren(onPhase).finally(() => {
    laufend = null;
  });

  return laufend;
}

async function durchfuehren(onPhase?: (phase: SyncPhase) => void): Promise<SyncOutcome> {
  const outcome: SyncOutcome = { status: 'ok', pushed: 0, pulled: 0, conflicts: 0 };

  // Bewusst kein Kurzschluss über `navigator.onLine`: Der Wert ist auf
  // Mobilgeräten unzuverlässig (WLAN ohne Internetzugang meldet „online“,
  // manche Systeme melden nach dem Aufwachen gar nichts). Ein Versuch
  // scheitert ohne Netz sofort und ohne Wartezeit – das ist der ehrlichere
  // Test als die Angabe des Browsers.
  try {
    onPhase?.('pruefe');
    const { user } = await api.session();
    if (!user) {
      return { ...outcome, status: 'anmeldung-noetig', message: 'Die Sitzung ist abgelaufen.' };
    }

    onPhase?.('sende');
    const pushResult = await sendeWarteschlange();
    outcome.pushed = pushResult.pushed;
    outcome.conflicts = pushResult.conflicts;

    onPhase?.('empfange');
    outcome.pulled = await holeAenderungen();

    await writeMeta(META_KEYS.lastSyncAt, nowIso());

    return outcome;
  } catch (error) {
    if (error instanceof NetworkError) return { ...outcome, status: 'offline' };
    if (error instanceof ApiError && error.isUnauthorized) {
      return { ...outcome, status: 'anmeldung-noetig', message: 'Die Sitzung ist abgelaufen.' };
    }

    return {
      ...outcome,
      status: 'fehler',
      message: error instanceof Error ? error.message : 'Unbekannter Synchronisationsfehler.',
    };
  } finally {
    onPhase?.('idle');
  }
}

// ---------------------------------------------------------------------------
// Senden
// ---------------------------------------------------------------------------

async function sendeWarteschlange(): Promise<{ pushed: number; conflicts: number }> {
  let pushed = 0;
  let conflicts = 0;

  // Datensatzänderungen zuerst – ein Foto braucht seinen Auftrag auf dem Server.
  const entries = await db.outbox.orderBy('createdAt').toArray();
  const mutations = entries.filter((entry) => entry.op !== 'upload');
  const uploads = entries.filter((entry) => entry.op === 'upload');

  for (let index = 0; index < mutations.length; index += PUSH_BATCH_SIZE) {
    const batch = mutations.slice(index, index + PUSH_BATCH_SIZE);
    const response = await api.push(batch);

    for (const result of response.results) {
      const entry = batch.find((candidate) => candidate.id === result.mutationId);
      if (!entry) continue;

      const verarbeitet = await verarbeiteErgebnis(entry, result);
      if (verarbeitet === 'ok') pushed += 1;
      if (verarbeitet === 'konflikt') conflicts += 1;
    }
  }

  for (const entry of uploads) {
    const erfolgreich = await ladeFotoHoch(entry);
    if (erfolgreich) pushed += 1;
  }

  return { pushed, conflicts };
}

async function verarbeiteErgebnis(
  entry: OutboxEntry,
  result: SyncMutationResult,
): Promise<'ok' | 'konflikt' | 'offen'> {
  if (result.status === 'applied' || result.status === 'duplicate') {
    await db.transaction('rw', [tableFor(entry.entity) as never, db.outbox], async () => {
      const record = (await tableFor(entry.entity).get(entry.recordId)) as
        | (Record<string, unknown> & { id: string })
        | undefined;

      if (record) {
        await tableFor(entry.entity).put({ ...record, version: result.version ?? record.version } as never);
      }
      await db.outbox.delete(entry.id);
    });

    return 'ok';
  }

  if (result.status === 'conflict') {
    await behandleKonflikt(entry, result);
    return 'konflikt';
  }

  // 'rejected': Der Server hat die Daten nicht angenommen. Der lokale Stand
  // bleibt erhalten; nach mehreren Versuchen wird es sichtbar gemeldet.
  const attempts = entry.attempts + 1;
  await db.outbox.update(entry.id, { attempts, lastError: result.message ?? 'Vom Server abgelehnt.' });

  if (attempts >= MAX_ATTEMPTS_BEFORE_REPORT) {
    await schreibeKonflikt({
      entity: entry.entity,
      recordId: entry.recordId,
      message: `Änderung wird vom Server abgelehnt: ${result.message ?? 'unbekannter Grund'}`,
      localRecord: entry.payload,
      serverRecord: null,
    });
  }

  return 'offen';
}

/**
 * Konflikt: Der Serverstand wird lokale Grundlage, damit weitergearbeitet
 * werden kann. Der eigene, nicht übernommene Stand bleibt im
 * Konfliktdatensatz sichtbar und kann von Hand übernommen werden.
 */
async function behandleKonflikt(entry: OutboxEntry, result: SyncMutationResult): Promise<void> {
  const lokal = (await tableFor(entry.entity).get(entry.recordId)) as Record<string, unknown> | undefined;

  await db.transaction('rw', [tableFor(entry.entity) as never, db.outbox, db.conflicts], async () => {
    if (result.serverRecord) {
      await tableFor(entry.entity).put(bereinige(result.serverRecord) as never);
    }
    await db.outbox.delete(entry.id);
  });

  await schreibeKonflikt({
    entity: entry.entity,
    recordId: entry.recordId,
    message: result.message ?? 'Der Datensatz wurde zwischenzeitlich geändert.',
    localRecord: (lokal ?? entry.payload) as Record<string, unknown> | null,
    serverRecord: result.serverRecord ?? null,
  });
}

async function schreibeKonflikt(input: {
  entity: SyncEntity;
  recordId: string;
  message: string;
  localRecord: Record<string, unknown> | null;
  serverRecord: Record<string, unknown> | null;
}): Promise<void> {
  const orderId = await ermittleAuftragsId(input.entity, input.serverRecord ?? input.localRecord);

  const conflict: ConflictEntry = {
    id: newId(),
    entity: input.entity,
    recordId: input.recordId,
    orderId,
    message: input.message,
    localRecord: input.localRecord,
    serverRecord: input.serverRecord,
    createdAt: nowIso(),
    resolvedAt: null,
  };

  await db.conflicts.put(conflict);
}

async function ermittleAuftragsId(
  entity: SyncEntity,
  record: Record<string, unknown> | null,
): Promise<string | null> {
  if (!record) return null;
  if (entity === 'orders') return String(record.id);
  if (typeof record.orderId === 'string') return record.orderId;

  return null;
}

async function ladeFotoHoch(entry: OutboxEntry): Promise<boolean> {
  const photo = await db.photos.get(entry.recordId);
  const blobEntry = await db.photoBlobs.get(entry.recordId);

  if (!photo || !blobEntry) {
    // Nichts mehr hochzuladen (z. B. Foto wurde gelöscht) – Eintrag entfernen.
    await db.outbox.delete(entry.id);
    return false;
  }

  try {
    const response = await api.uploadPhoto(
      {
        id: photo.id,
        orderId: photo.orderId,
        positionId: photo.positionId,
        note: photo.note,
        width: photo.width,
        height: photo.height,
        createdAt: photo.createdAt,
      },
      blobEntry.blob,
    );

    await db.transaction('rw', [db.photos, db.photoBlobs, db.outbox], async () => {
      await db.photos.put(bereinige(response.photo) as never);
      await db.photoBlobs.put({ ...blobEntry, uploaded: true });
      await db.outbox.delete(entry.id);
    });

    return true;
  } catch (error) {
    if (error instanceof NetworkError) throw error;

    const attempts = entry.attempts + 1;
    const message = error instanceof Error ? error.message : 'Upload fehlgeschlagen.';
    await db.outbox.update(entry.id, { attempts, lastError: message });

    if (attempts >= MAX_ATTEMPTS_BEFORE_REPORT) {
      await schreibeKonflikt({
        entity: 'photos',
        recordId: entry.recordId,
        message: `Foto kann nicht hochgeladen werden: ${message}`,
        localRecord: null,
        serverRecord: null,
      });
    }

    return false;
  }
}

// ---------------------------------------------------------------------------
// Empfangen
// ---------------------------------------------------------------------------

async function holeAenderungen(): Promise<number> {
  let cursor = await readMeta<string>(META_KEYS.syncCursor, '0');
  let total = 0;

  for (let page = 0; page < MAX_PULL_PAGES; page += 1) {
    const response = await api.pull(cursor);
    total += await seiteUebernehmen(response.changes);

    cursor = response.cursor;
    await writeMeta(META_KEYS.syncCursor, cursor);

    if (!response.hasMore) break;
  }

  return total;
}

/**
 * Übernimmt eine Seite Serveränderungen in einer einzigen Transaktion.
 *
 * Die Liste der noch offenen lokalen Änderungen wird **innerhalb** der
 * Transaktion gelesen. Damit kann eine Eingabe, die währenddessen auf der
 * Baustelle gemacht wird, nicht zwischen Prüfung und Schreiben verloren
 * gehen. Geschrieben wird gebündelt per `bulkPut` statt Zeile für Zeile –
 * bei einer Erstsynchronisation mit einigen tausend Datensätzen ist das der
 * Unterschied zwischen einem Sekundenbruchteil und spürbarem Ruckeln.
 */
async function seiteUebernehmen(changes: SyncPullResponse['changes']): Promise<number> {
  const tabellen = SYNC_ENTITIES.map((entity) => TABLE_BY_ENTITY[entity]);

  return db.transaction('rw', [...tabellen, db.outbox] as never, async () => {
    const offene = new Set(
      (await db.outbox.toArray()).map((entry) => `${entry.entity}:${entry.recordId}`),
    );

    let uebernommen = 0;

    for (const entity of SYNC_ENTITIES) {
      const rows = (changes[entity] ?? [])
        // Lokale, noch nicht übertragene Änderung hat Vorrang – sonst
        // verschwände die Eingabe des Anwenders beim nächsten Abgleich.
        .filter((row) => !offene.has(`${entity}:${String(row.id)}`))
        .map(bereinige);

      if (rows.length === 0) continue;

      await tableFor(entity).bulkPut(rows as never);
      uebernommen += rows.length;
    }

    return uebernommen;
  });
}

/** Entfernt serverinterne Felder, die lokal keine Bedeutung haben. */
function bereinige(row: Record<string, unknown>): Record<string, unknown> {
  const { seq, ...rest } = row;
  void seq;
  return rest;
}

// ---------------------------------------------------------------------------
// Konflikte
// ---------------------------------------------------------------------------

export async function konfliktUebernehmen(conflictId: string): Promise<void> {
  const conflict = await db.conflicts.get(conflictId);
  if (!conflict?.localRecord) return;

  const aktuell = (await tableFor(conflict.entity).get(conflict.recordId)) as
    | (Record<string, unknown> & { version?: number })
    | undefined;

  // Eigenen Stand auf Basis der jetzt gültigen Serverversion erneut einreichen.
  const { saveRecord } = await import('../offline/repository.js');
  await saveRecord(conflict.entity, {
    ...(conflict.localRecord as Record<string, unknown>),
    id: conflict.recordId,
    version: aktuell?.version ?? 0,
  } as { id: string; version: number });

  await db.conflicts.update(conflictId, { resolvedAt: nowIso() });
}

export async function konfliktVerwerfen(conflictId: string): Promise<void> {
  await db.conflicts.update(conflictId, { resolvedAt: nowIso() });
}
